import { useMemo, useState } from 'react';
import { useCustom } from '@refinedev/core';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { format, parseISO, subDays } from 'date-fns';
import { ru } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  TrendingUp,
  ShoppingCart,
  Users,
  Receipt,
  RotateCcw,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DateRangePicker } from '@/components/ui/date-range-picker';

const PROVIDER_LABELS: Record<string, string> = {
  MANUAL: 'Ручной',
  CLICK: 'Click',
  PAYME: 'Payme',
  STRIPE: 'Stripe',
  MULTICARD: 'Multicard',
  CLOUDPAYMENTS: 'CloudPayments',
};

const PROVIDER_COLORS: Record<string, string> = {
  MANUAL: '#6b7280',
  CLICK: '#3b82f6',
  PAYME: '#06b6d4',
  STRIPE: '#8b5cf6',
  MULTICARD: '#10b981',
  CLOUDPAYMENTS: '#f59e0b',
};

interface ChannelPassAnalyticsTier {
  tierId: number | null;
  tierLabel: string;
  durationDays: number | null;
  purchases: number;
  uniqueBuyers: number;
  revenue: number;
  share: number;
}

interface ChannelPassAnalyticsProvider {
  provider: string;
  count: number;
  revenue: number;
}

interface ChannelPassAnalyticsDay {
  date: string;
  revenue: number;
  purchases: number;
}

interface ChannelPassAnalytics {
  baseCurrency: string;
  summary: {
    totalRevenue: number;
    totalPurchases: number;
    uniqueBuyers: number;
    averageCheck: number;
    refundedCount: number;
    refundedRevenue: number;
  };
  byTier: ChannelPassAnalyticsTier[];
  byProvider: ChannelPassAnalyticsProvider[];
  revenueByDay: ChannelPassAnalyticsDay[];
}

interface DateRange {
  from?: Date;
  to?: Date;
}

function getDefaultRange(): DateRange {
  const to = new Date();
  const from = subDays(to, 30);
  return { from, to };
}

function formatMoney(value: number, currency: string): string {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency,
    maximumFractionDigits: currency === 'UZS' ? 0 : 2,
  }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('ru-RU').format(value);
}

export function ChannelPassAnalyticsTab({ passId }: { passId: number }) {
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultRange);

  const params = useMemo(() => {
    if (!dateRange.from || !dateRange.to) {
      return null;
    }
    return {
      startDate: dateRange.from.toISOString(),
      endDate: dateRange.to.toISOString(),
    };
  }, [dateRange]);

  const { query } = useCustom<ChannelPassAnalytics>({
    url: `/channel-passes/admin/${passId}/analytics`,
    method: 'get',
    config: {
      query: params ?? {},
    },
    queryOptions: {
      enabled: params !== null,
      retry: false,
    },
    errorNotification: (error) => ({
      message: error?.message ?? 'Не удалось загрузить аналитику',
      type: 'error',
    }),
  });

  const data = query.data?.data;
  const isLoading = query.isLoading || query.isFetching;
  const isError = query.isError;
  const currency = data?.baseCurrency ?? 'UZS';

  const handleRetry = () => {
    void query.refetch();
    toast.info('Обновляем аналитику…');
  };

  return (
    <div className="space-y-4 pt-4">
      {/* Date range */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="flex-1 max-w-md">
          <DateRangePicker
            value={dateRange.from && dateRange.to ? { from: dateRange.from, to: dateRange.to } : null}
            onChange={(range) => {
              if (range?.from && range.to) {
                setDateRange({ from: range.from, to: range.to });
              }
            }}
          />
        </div>
        <Button variant="outline" size="sm" onClick={handleRetry} disabled={isLoading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Обновить
        </Button>
      </div>

      {/* Error state */}
      {isError && (
        <Card className="border-destructive/50">
          <CardContent className="flex items-center gap-3 py-4">
            <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium">Ошибка загрузки аналитики</p>
              <p className="text-xs text-muted-foreground">
                Проверьте соединение и попробуйте ещё раз.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={handleRetry}>
              Повторить
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Loading skeleton */}
      {isLoading && !data && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {['rev', 'sales', 'buyers', 'avg', 'ref'].map((k) => (
            <Skeleton key={k} className="h-24 rounded-xl" />
          ))}
        </div>
      )}

      {data && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <SummaryCard
              icon={<TrendingUp className="h-4 w-4" />}
              label="Выручка"
              value={formatMoney(data.summary.totalRevenue, currency)}
              hint={`${formatNumber(data.summary.totalPurchases)} продаж`}
              accent="text-green-600 dark:text-green-500"
            />
            <SummaryCard
              icon={<ShoppingCart className="h-4 w-4" />}
              label="Продажи"
              value={formatNumber(data.summary.totalPurchases)}
              hint="Оплаченных заказов"
            />
            <SummaryCard
              icon={<Users className="h-4 w-4" />}
              label="Покупатели"
              value={formatNumber(data.summary.uniqueBuyers)}
              hint="Уникальных"
              accent="text-blue-600 dark:text-blue-500"
            />
            <SummaryCard
              icon={<Receipt className="h-4 w-4" />}
              label="Средний чек"
              value={formatMoney(data.summary.averageCheck, currency)}
              hint="На продажу"
            />
            <SummaryCard
              icon={<RotateCcw className="h-4 w-4" />}
              label="Возвраты"
              value={formatNumber(data.summary.refundedCount)}
              hint={data.summary.refundedRevenue > 0
                ? formatMoney(data.summary.refundedRevenue, currency)
                : 'За период'}
              accent={data.summary.refundedCount > 0 ? 'text-red-600 dark:text-red-500' : undefined}
            />
          </div>

          {/* Revenue by day chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Выручка по дням</CardTitle>
            </CardHeader>
            <CardContent>
              {data.revenueByDay.length === 0 ? (
                <div className="flex items-center justify-center h-[240px] text-sm text-muted-foreground">
                  Нет подтверждённых платежей за выбранный период
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart
                    data={data.revenueByDay.map((d) => ({
                      ...d,
                      formattedDate: format(parseISO(d.date), 'd MMM', { locale: ru }),
                    }))}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="channelPassRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="formattedDate"
                      tickLine={false}
                      axisLine={false}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      tick={{ fontSize: 12 }}
                      tickFormatter={(v) =>
                        new Intl.NumberFormat('ru-RU', {
                          notation: 'compact',
                          compactDisplay: 'short',
                        }).format(v)
                      }
                      width={60}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: '1px solid hsl(var(--border))',
                        background: 'hsl(var(--popover))',
                        fontSize: 12,
                      }}
                      formatter={(value) => [
                        formatMoney(typeof value === 'number' ? value : 0, currency),
                        'Выручка',
                      ]}
                    />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      stroke="#22c55e"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#channelPassRevenue)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Two-column row: tiers table + provider pie */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Продажи по тарифам</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {data.byTier.length === 0 ? (
                  <div className="flex items-center justify-center h-[240px] text-sm text-muted-foreground">
                    Нет продаж за период
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Тариф</TableHead>
                        <TableHead className="text-right">Продано</TableHead>
                        <TableHead className="text-right">Покупателей</TableHead>
                        <TableHead className="text-right">Выручка</TableHead>
                        <TableHead className="w-[120px]">Доля</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.byTier.map((t) => (
                        <TableRow key={t.tierId ?? 'none'}>
                          <TableCell className="font-medium">{t.tierLabel}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatNumber(t.purchases)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatNumber(t.uniqueBuyers)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums font-medium">
                            {formatMoney(t.revenue, currency)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Progress value={Math.round(t.share * 100)} className="h-1.5" />
                              <span className="text-[11px] text-muted-foreground tabular-nums w-9 text-right">
                                {Math.round(t.share * 100)}%
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">По провайдерам оплаты</CardTitle>
              </CardHeader>
              <CardContent>
                {data.byProvider.length === 0 ? (
                  <div className="flex items-center justify-center h-[240px] text-sm text-muted-foreground">
                    Нет данных
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie
                        data={data.byProvider.map((p) => ({
                          name: PROVIDER_LABELS[p.provider] ?? p.provider,
                          value: p.revenue,
                          provider: p.provider,
                          count: p.count,
                        }))}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                      >
                        {data.byProvider.map((p) => (
                          <Cell
                            key={p.provider}
                            fill={PROVIDER_COLORS[p.provider] ?? '#6b7280'}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          borderRadius: 8,
                          border: '1px solid hsl(var(--border))',
                          background: 'hsl(var(--popover))',
                          fontSize: 12,
                        }}
                        formatter={(value) =>
                          formatMoney(typeof value === 'number' ? value : 0, currency)
                        }
                      />
                      <Legend
                        verticalAlign="bottom"
                        iconType="circle"
                        wrapperStyle={{ fontSize: 12 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

interface SummaryCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint: string;
  accent?: string;
}

function SummaryCard({ icon, label, value, hint, accent }: SummaryCardProps) {
  return (
    <div className="rounded-xl border bg-card p-4 flex items-start gap-3">
      <div className="text-muted-foreground shrink-0 mt-0.5">{icon}</div>
      <div className="min-w-0 flex-1">
        <p className={`text-xl font-semibold truncate ${accent ?? ''}`}>{value}</p>
        <p className="text-[11px] text-muted-foreground truncate">{label}</p>
        <p className="text-[10px] text-muted-foreground truncate mt-0.5">{hint}</p>
      </div>
    </div>
  );
}
